import React from 'react'
import { Link, useParams } from 'react-router-dom'
import './setup.css'
import Style from './setupPage.module.css'
import InstallerAction from '../../Modules/NewConnector/Installers/InstallerAction'
import ConnectorIcon from '../../SharedComponents/ConnectorIcon/ConnectorIcon'
import PageLoader from '../../SharedComponents/PageState/PageLoader'
import PageNotice from '../../SharedComponents/PageState/PageNotice'
import { describeConnection, useGetConnectionInstanceQuery } from '../../../features/connections/connectionsApi'
import { daysUntilExpiry } from '../../../features/connections/connectionStatus'
import { isHostedEndpoint } from '../../../features/installers/useInstallerAction'
import { PATH, paths } from '../../../app/routes'

const hasValue = (value) => value !== undefined && value !== null && String(value).trim() !== ''

const SIDE_FLAGS = {
    source: { installed: 'isSourceInstalled', authenticated: 'isSourceAuthenticated' },
    target: { installed: 'isTargetInstalled', authenticated: 'isTargetAuthenticated' }
}

const RESOURCES = [
    { key: 'installerGuide', label: 'Installation guide', icon: 'uil-book-open' },
    { key: 'preRequisites', label: 'Prerequisites', icon: 'uil-clipboard-notes' },
    { key: 'userManual', label: 'User manual', icon: 'uil-file-alt' }
]

const endpointProgress = (instance, side) => ({
    isInstalled: Boolean(instance[SIDE_FLAGS[side].installed]),
    isAuthenticated: Boolean(instance[SIDE_FLAGS[side].authenticated])
})

const StepCard = ({ connection, instance, side }) => {
    const endpoint = connection[side]
    const isHosted = isHostedEndpoint(endpoint)
    const { isInstalled, isAuthenticated } = endpointProgress(instance, side)
    const needsAuthentication = endpoint.isAuthenticationRequired && !isAuthenticated
    const size = endpoint.installer ? endpoint.installer.size : null
    const showVersion = hasValue(endpoint.version) && endpoint.version !== endpoint.name && endpoint.version !== endpoint.shortName
    const details = [
        { label: 'Deployment', value: isHosted ? 'Cloud hosted' : 'On-premise' },
        { label: 'Version', value: showVersion ? endpoint.version : 'Latest' },
        hasValue(size) ? { label: isHosted ? 'Package size' : 'File size', value: size } : null
    ].filter(Boolean)
    const resources = RESOURCES.filter((item) => hasValue(endpoint[item.key]))

    return (
        <article className={[Style.stepCard, isInstalled ? Style.stepDone : ''].join(' ')}>
            <div className={Style.endpoint}>
                <span className={Style.endpointIcon}>
                    <ConnectorIcon shortName={endpoint.shortName} fallbackClassName={Style.endpointFallback} decorative />
                </span>
                <h2 className={Style.endpointName} title={endpoint.name}>{endpoint.name}</h2>
                <span className={[Style.stepState, isInstalled ? Style.stepStateDone : ''].join(' ')}>
                    <i className={isInstalled ? 'uil-check-circle' : 'uil-circle'} aria-hidden="true"></i>
                    {isInstalled ? 'Installed' : 'Not installed'}
                </span>
            </div>
            <dl className={Style.details}>
                {details.map((item) => (
                    <div key={item.label} className={Style.detail}>
                        <dt>{item.label}</dt>
                        <dd>{item.value}</dd>
                    </div>
                ))}
            </dl>
            {resources.length > 0 ? (
                <ul className={Style.resources}>
                    {resources.map((item) => (
                        <li key={item.key}>
                            <a href={endpoint[item.key]} target="_blank" rel="noopener noreferrer">
                                <i className={item.icon} aria-hidden="true"></i>{item.label}
                            </a>
                        </li>
                    ))}
                </ul>
            ) : null}
            {needsAuthentication ? (
                <div className={Style.notice} role="note">
                    <i className="uil-lock-alt" aria-hidden="true"></i>
                    <span>
                        {endpoint.name} must be authenticated before data can sync.{' '}
                        <Link to={paths.authentication(connection.id, instance.id)}>Authenticate now</Link>
                    </span>
                </div>
            ) : null}
            <div className={Style.action}>
                <InstallerAction connection={connection} side={side} className={`btn btn-primary ${Style.button}`} />
            </div>
        </article>
    )
}

/** Download or set up a connector's installers: /Setup/:connectorId/:instanceId */
const Setup = () => {
    const { connectorId, instanceId } = useParams()
    const { data: connection, isLoading, isError } = useGetConnectionInstanceQuery({ connectorId, instanceId })

    if (isLoading) {
        return <PageLoader message="Loading installers..." />
    }
    if (isError || !connection) {
        return (
            <PageNotice
                title="Connector not found"
                hint="It may have been removed, or the link is incomplete."
                linkTo={PATH.MY_PRODUCTS}
                linkLabel="Go to My Products"
            />
        )
    }

    const connector = describeConnection(connection)
    const title = connector.displayName || `${connector.source.name} ⇄ ${connector.target.name}`
    const days = connector.expiryDate ? daysUntilExpiry(connection) : null
    const instance = connector.instance
    const installedCount = ['source', 'target'].filter((side) => endpointProgress(instance, side).isInstalled).length

    return (
        <div className="main-content mn-dashboard">
            <div className="page-content custompaddingdash">
                <div className={`container-fluid ${Style.page}`}>
                    <header className={Style.header}>
                        <div className={Style.heading}>
                            <ol className={`breadcrumb ${Style.breadcrumb}`}>
                                <li className="breadcrumb-item"><Link to={PATH.DASHBOARD}>Dashboard</Link></li>
                                <li className="breadcrumb-item"><Link to={paths.productDetail(connectorId)}>{title}</Link></li>
                                <li className="breadcrumb-item active" aria-current="page">Setup</li>
                            </ol>
                            <h1 className={Style.title}>{title}</h1>
                        </div>
                        <div className={Style.status}>
                            <span className={[Style.badge, connector.isSetupCompleted ? Style.badgeSuccess : Style.badgePending].join(' ')}>
                                <i className={connector.isSetupCompleted ? 'uil-check-circle' : 'uil-clock'} aria-hidden="true"></i>
                                {connector.isSetupCompleted ? 'Setup complete' : `${installedCount} of 2 installed`}
                            </span>
                            {instance.instanceType ? (
                                <span className={[Style.badge, Style.badgeNeutral].join(' ')}>
                                    <i className="uil-server-network" aria-hidden="true"></i>
                                    {instance.instanceType}
                                </span>
                            ) : null}
                            {connector.featureName ? (
                                <span className={[Style.badge, Style.badgeNeutral].join(' ')}>
                                    <i className="uil-award" aria-hidden="true"></i>
                                    {connector.featureName} plan
                                </span>
                            ) : null}
                            {days !== null ? (
                                <span className={[Style.badge, days <= 30 ? Style.badgeWarning : Style.badgeNeutral].join(' ')}>
                                    <i className="uil-calendar-alt" aria-hidden="true"></i>
                                    {days < 1 ? 'Subscription expired' : `${days} ${days === 1 ? 'day' : 'days'} left`}
                                </span>
                            ) : null}
                        </div>
                    </header>

                    <section className={Style.steps} aria-label="Setup steps">
                        <StepCard connection={connection} instance={instance} side="source" />
                        <div className={Style.link} aria-hidden="true"><i className="uil-exchange"></i></div>
                        <StepCard connection={connection} instance={instance} side="target" />
                    </section>
                </div>
            </div>
        </div>
    )
}

export default Setup
